import React, { useEffect, useState } from 'react';
import {
  Typography,
  Grid,
  CircularProgress,
  Button,
  Box,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ListAlt } from '@mui/icons-material';
import { useCartContext } from '@context/CartContext';
import theme from '@styles/theme';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@context/AuthContext';
import QrCodeDialog from './QrCodeDialog';
import { useLambdaFunctionContext } from '@context/LambdaFunctionContext';
import { ObjectId } from 'bson';

import { useHistory } from 'react-router-dom';
import URLS from '@/URLS';
import { useGeneralContext } from '@context/GeneralContext';


import { sendPostMessageToParent } from '@functions/trafficController';
import { useStoreAppColor } from '@state/useStoreAppColor';
import { useStoreOrderNumber } from '@state/useStoreOrderNumber';
import { useStoreViewBill } from '@state/useStoreViewBill';
import { useStoreSettings } from '@state/useStoreSettings';
import { useStoreOutlet } from '@state/useStoreOutlet';
import { useStoreCartItems } from '@state/useStoreCartItems';
import { useConfirmDialog } from '@state/checkout/useConfirmDialog';
import { styled } from '@mui/system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import useRxdbStoreLog from '@hooks/useRxdbStoreLog';
import { useMiniappConfirm } from '@state/useMiniappConfirm';
import { useMiniappReq } from '@state/useMiniappReq';
import localStorageNames from '@data/localStorageNames';
import useRxdbMiniappReq from '@hooks/useRxdbMiniappReq';
import { insertMiniappStoreLogUtil } from '@hooks/useInsertMiniappStoreLog';
import useHITApi from '@hooks/useHITApi';

const useStyles = makeStyles({
  titleStyle: {
    paddingBottom: 0,
  },
  displayFlex: {
    display: 'flex',
    justifyContent: 'center',
  },
  buttonNumber: {
    margin: 7,
    height: 60,
    '& .MuiButton-label': {
      width: '100%',
      fontSize: 12,
    },
  },
  button: {
    width: '200px',
    fontWeight: 'bold',
  },
  confirmText: {
    textAlign: 'center',
    whiteSpace: 'pre-line',
  },
  emailReceipt: {
    backgroundColor: '#FFFFFF',
    width: '100%',
    '& .MuiInputLabel-root': {
      color: (props) => props.primaryFontColor,
    },
    '& ::placeholder': {
      color: theme.palette.text.disabled,
    },
    '& label.Mui-focused': {
      color: (props) => props.primaryFontColor,
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: (props) => props.primaryFontColor,
      },
    },
  },
  iconDialog: {
    display: 'flex',
  },
  iconDialog2: {
    position: 'absolute',
    top: -65,
    right: -5,
    fontSize: 30,
  },
});

const ModalOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw', // Cover the entire viewport width
  height: '100vh', // Cover the entire viewport height
  backgroundColor: 'rgba(0,0,0,.5)', // Semi-transparent background
  display: 'flex',
  justifyContent: 'center', // Center horizontally
  alignItems: 'center', // Center vertically
  zIndex: 1400,
}));

const ModalContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center', // Corrected the typo from 'alignItem'
  backgroundColor: 'white',
  width: '95%', // You can adjust this to your preferred width
  maxWidth: '600px', // Optional: Limit the maximum width
  padding: '16px', // Optional: Add padding
  borderRadius: theme.shape.borderRadius, // Optional: Rounded corners
  overflowY: 'auto', // Allow scrolling if content overflows
}));

const SuccessConfirm = () => {
  const {
    getContextState,
    setContextState,
    getResultCode: resultCode,
    forceReset,
  } = useCartContext();
  const paymentVisible = getContextState('paymentResId').visible;
  const { t } = useTranslation();
  const { appColor } = useStoreAppColor((state) => ({
    appColor: state.appColor,
  }));
  const classes = useStyles(appColor);
  //TODO: Gunakan state untuk getSettting & getOutletInfo
  useAuthContext();
  const { sendEmailBill } = useLambdaFunctionContext();
  const { openErrorSnackBar, showLoading } = useGeneralContext();
  const history = useHistory();
  const [emailReceipt, setEmailReceipt] = useState('');
  const [isErrorTextField, setErrorTextField] = useState(false);

  //TODO: Ubah orderNumber ke state
  const { orderNumber } = useStoreOrderNumber((state) => ({
    orderNumber: state.orderNumber,
  }));
  const { outlet: outletInfo } = useStoreOutlet((state) => ({
    outlet: state.outlet,
  }));
  const { getSetting } = useStoreSettings((state) => ({
    getSetting: state.getSetting,
  }));

  const { viewBillId: transId, removeIsViewBill } = useStoreViewBill(
    (state) => ({
      viewBillId: state.viewBillId,
      removeIsViewBill: state.removeIsViewBill,
    }),
  );

  const { clearCart } = useStoreCartItems((state) => ({
    clearCart: state.clearCart,
  }));

  const handleClearCart = () => {
    removeIsViewBill();
    // localStorage.removeItem(localStorageNames.IS_VIEW_BILL);
    clearCart();
    setContextState({
      paymentResId: {
        visible: false,
      },
    });
  };

  const setConfirmModal = (value, orderMethod, parameter) => {
    setContextState({
      confirmModal: {
        visible: value,
        orderMethod: orderMethod,
        parameter: parameter,
      },
    });
  };

  const closeModal = () => {
    setConfirmModal(false, null, null);
  };

  const handleConfirmBtn = () => {
    if (getSetting().orderMethod === 'payment_order') {
      if (resultCode() === 'Authorised') {
        forceReset();
        handleClearCart();
      } else {
        //close modal
        closeModal();
      }
    } else {
      forceReset();
    }
    setContextState({
      paymentResId: {
        visible: false,
      },
    });
  };

  const getNameSalesType = (paramId) => {
    if (paramId) {
      // let outletInfo = getOutletInfo;
      let salesTypes = outletInfo.salestype.sales;

      let index = salesTypes.findIndex(
        (x) => x.type_sales_id.toString() === paramId.toString(),
      );

      return salesTypes[index]?.title;
    }
    return null;
  };

  const handleSendEmail = async () => {
    const valid = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(
      emailReceipt,
    );
    if (valid) {
      showLoading(true);
      setErrorTextField(false);
      try {
        await sendEmailBill({
          transaction_id: transId,
          email: emailReceipt,
        });
      } catch (error) {
        console.error('Failed to send email:', error);
      } finally {
        showLoading(false);
        forceReset();
        handleClearCart();
      }
    } else {
      setErrorTextField(true);
      openErrorSnackBar('Please fill in your email validly');
    }
    setContextState({
      paymentResId: {
        visible: false,
      },
    });
  };

  return (
    <ModalOverlay>
      <ModalContainer>
        {getSetting().orderMethod === 'payment_order' ? (
          <Box
            className={classes.iconDialog}
            sx={{ position: 'relative', display: 'inline-block' }}>
            <ListAlt style={{ fontSize: 80 }} />
            {resultCode() === 'Authorised' ? (
              <CheckCircleIcon
                className={classes.iconDialog2}
                sx={{
                  color: theme.palette.success.main,
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  fontSize: 30,
                  transform: 'translate(50%, -50%)', // Adjust to fine-tune the position
                }}
              />
            ) : (
              <CancelIcon
                className={classes.iconDialog2}
                sx={{
                  color: 'red',
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  fontSize: 30,
                  transform: 'translate(50%, -50%)', // Adjust to fine-tune the position
                }}
              />
            )}
          </Box>
        ) : (
          <CheckCircleIcon
            sx={{ color: theme.palette.success.main, fontSize: '3rem' }}
          />
        )}
        <Grid
          direction="column"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            aligItems: 'center',
          }}>
          <Grid item>
            {getSetting().orderMethod === 'waiter_order' && (
              <Typography variant="h4" sx={{ textAlign: 'center' }}>
                {t('thankyou')}
              </Typography>
            )}

            <Typography sx={{ textAlign: 'center' }}>
              {getSetting().orderMethod === 'waiter_order' &&
                t('waiterOrderComplete')}
            </Typography>

            <Typography
              sx={{ textAlign: 'center', marginBottom: theme.spacing(6) }}>
              {getSetting().orderMethod === 'payment_order' &&
                //TODO: tampilkan order number dari lcoalstorage
                //custom table raptor/rdo
                (resultCode() === 'Authorised' ? (
                  <div>
                    Payment Successful, <br /> Your order has been confirmed and
                    sent to the kitchen!
                  </div>
                ) : (
                  'Payment Failed, Please try again !'
                ))}
            </Typography>
            {getSetting().orderMethod === 'payment_order' &&
              resultCode() === 'Authorised' &&
              (getSetting().customTable === 'raptor' ||
                getSetting().customTable === 'rdo') && (
                <Grid container direction="row" justifyContent="center">
                  {orderNumber ? (
                    <Typography sx={{ fontSize: '22px', fontWeight: 'bold' }}>
                      Order No : {orderNumber}
                    </Typography>
                  ) : (
                    <Grid item sx={{ margin: 'auto' }}>
                      <Button
                        onClick={() => {
                          showLoading(true);
                          setTimeout(() => {
                            handleConfirmBtn();
                            sendPostMessageToParent('path', URLS.PAYMENT_BILL);
                            history.replace(URLS.PAYMENT_BILL);
                          }, 3000);
                        }}
                        className={classes.buttonNumber}
                        sx={{
                          color: appColor.secondaryFontColor,
                          backgroundColor: appColor.primaryThemeColor,
                          '&:hover': {
                            backgroundColor: appColor.primaryThemeColor,
                          },
                        }}
                        variant="contained">
                        Click here to view
                        <br />
                        your order number
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
            <Typography sx={{ textAlign: 'center' }}>
              {getSetting().orderMethod === 'normal_order' &&
                t('orderComplete')}
            </Typography>
          </Grid>

          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 5,
              marginBottom: theme.spacing(6),
            }}>
            {getSetting().orderMethod === 'payment_order' &&
              resultCode() === 'Authorised' && (
                <Grid sx={{ width: '50%', paddingTop: theme.spacing(6) }}>
                  <Button
                    onClick={() => {
                      showLoading(true);
                      setTimeout(() => {
                        handleConfirmBtn();
                        sendPostMessageToParent('path', URLS.PAYMENT_BILL);
                        history.replace(URLS.PAYMENT_BILL);
                      }, 3000);
                    }}
                    className={classes.buttonConfirm}
                    sx={{
                      color: appColor.secondaryFontColor,
                      backgroundColor: appColor.primaryThemeColor,
                      height:
                        getSetting().orderMethod === 'payment_order'
                          ? 60
                          : 'auto',
                      width: '100%',
                      '&:hover': {
                        backgroundColor: appColor.primaryThemeColor,
                      },
                    }}
                    variant="contained">
                    {/* TODO: di ganti view order untuk custom table raptor/rdo */}
                    {getSetting().customTable === 'raptor' ||
                      getSetting().customTable === 'rdo' ? (
                      <>
                        {' '}
                        View <br /> Order{' '}
                      </>
                    ) : (
                      <>
                        {' '}
                        View <br /> Receipt{' '}
                      </>
                    )}
                  </Button>
                </Grid>
              )}

            <Grid
              sx={{
                width:
                  getSetting().orderMethod === 'payment_order' &&
                    resultCode() === 'Authorised'
                    ? '50%'
                    : '100%',
                paddingTop: theme.spacing(6),
              }}
              id="continueBrowsing">
              <Button
                id="confirmSuccess"
                onClick={handleConfirmBtn}
                className={classes.buttonConfirm}
                sx={{
                  color: appColor.secondaryFontColor,
                  backgroundColor: appColor.primaryThemeColor,
                  margin: 'auto',
                  height:
                    getSetting().orderMethod === 'payment_order' ? 60 : 'auto',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: appColor.primaryThemeColor,
                  },
                }}
                variant="contained">
                {getSetting().orderMethod === 'payment_order' ? (
                  <>
                    {' '}
                    Continue <br /> Browsing{' '}
                  </>
                ) : (
                  t('confirm')
                )}
              </Button>
            </Grid>
          </Grid>
          {paymentVisible && resultCode() === 'Authorised' && (
            <Grid item>
              <TextField
                inputProps={{ maxLength: 36 }}
                className={classes.emailReceipt}
                autoComplete="off"
                id="emailReceipt"
                label="Email receipt"
                placeholder="Email receipt"
                variant="outlined"
                size="small"
                type="email"
                required={true}
                error={isErrorTextField}
                onChange={(event) => {
                  setEmailReceipt(event.target.value);
                }}
              />
              <Grid item sx={{ width: '100%' }}>
                <Button
                  onClick={() => {
                    setTimeout(async () => {
                      handleSendEmail();
                    }, 1500);
                  }}
                  sx={{
                    color: appColor.secondaryFontColor,
                    backgroundColor: appColor.primaryThemeColor,
                    width: '100%',
                    marginTop: 3,
                    '&:hover': {
                      backgroundColor: appColor.primaryThemeColor,
                    },
                  }}
                  variant="contained">
                  Send Email
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </ModalContainer>
    </ModalOverlay>
  );
};

const ConfirmDialog = ({ closeQRcode, confirmTransaction }) => {
  const { getContextState } = useCartContext();
  const paymentVisible = getContextState('paymentResId').visible;

  const { t } = useTranslation();
  const { confirmed, setConfirmed, openQRcode, setOpenQRcode } =
    useConfirmDialog((state) => ({
      confirmed: state.confirmed,
      setConfirmed: state.setConfirmed,
      openQRcode: state.openQRcode,
      setOpenQRcode: state.setOpenQRcode,
    }));

  //TODO: Change setting to state
  const { getSetting } = useStoreSettings((state) => ({
    getSetting: state.getSetting,
  }));

  const handleCloseQRcode = () => {
    setOpenQRcode({ visible: false, data: null });
    closeQRcode();
  };

  useEffect(() => {
    setConfirmed(paymentVisible);
    // eslint-disable-next-line
  }, [paymentVisible]);

  return (
    <>
      {!confirmed &&
        <ModalOverlay>
          <ModalContainer>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.125rem !important',
                fontWeight: 'bold',
                marginBottom: theme.spacing(4),
              }}>
              {t('confirmOrder').toUpperCase()}
            </Typography>
            <ConfirmDialogContent confirmTransaction={confirmTransaction} />
          </ModalContainer>
        </ModalOverlay>
      }
      {getSetting().orderMethod === 'quick_order'
        ? confirmed &&
        openQRcode.visible && (
          <QrCodeDialog
            closeModal={() => handleCloseQRcode()}
            data={openQRcode.data}
          />
        )
        : confirmed && <SuccessConfirm />}
    </>
  );
};

const ConfirmDialogContent = ({ confirmTransaction }) => {
  const { getContextState, setContextState } = useCartContext();
  const messageDialog = getContextState('confirmPayment').messageDialog;
  const { setConfirmed, setOpenQRcode } = useConfirmDialog((state) => ({
    confirmed: state.confirmed,
    setConfirmed: state.setConfirmed,
    openQRcode: state.openQRcode,
    setOpenQRcode: state.setOpenQRcode,
  }));
  const { appColor } = useStoreAppColor((state) => ({
    appColor: state.appColor,
  }));
  const setConfirmModal = (value, orderMethod, parameter) => {
    setContextState({
      confirmModal: {
        visible: value,
        orderMethod: orderMethod,
        parameter: parameter,
      },
    });
  };

  const closeModal = () => {
    setConfirmModal(false, null, null);
  };

  const classes = useStyles(appColor);
  const { t } = useTranslation();
  const [isButtonClick, setButtonClick] = useState(false);
  const [stateTransId, setStateTransId] = useState('');
  const [stateTransParameter, setstateTransParameter] = useState('');
  const [isTransactionProcessed, setIsTransactionProcessed] = useState(false);
  const { getLocalState } = useLambdaFunctionContext();
  const { openErrorSnackBar } = useGeneralContext();
  const [, subscribeConfirm] = useRxdbStoreLog();

  const { getMiniappConfirm, updateMiniappConfirm, updateMiniappConfirmId } = useMiniappConfirm(
    (state) => ({
      getMiniappConfirm: state.getMiniappConfirm,
      updateMiniappConfirm: state.updateMiniappConfirm,
      updateMiniappConfirmId: state.updateMiniappConfirmId,
    }),
  );

  //TODO: Change setting to state
  const { getSetting } = useStoreSettings((state) => ({
    getSetting: state.getSetting,
  }));

  const { getMiniappReq, updateMiniappReq, updateMiniappReqId, getMiniappReqMessage, updateMiniappReqMessage } = useMiniappReq((state) => ({
    getMiniappReq: state.getMiniappReq,
    updateMiniappReq: state.updateMiniappReq,
    updateMiniappReqId: state.updateMiniappReqId,
    getMiniappReqMessage: state.getMiniappReqMessage,
    updateMiniappReqMessage: state.updateMiniappReqMessage,
  }));

  const {
    updateViewBillId,
  } = useStoreViewBill((state) => ({
    updateViewBillId: state.updateViewBillId,
  }));

  const { clearCart } = useStoreCartItems((state) => ({
    clearCart: state.clearCart,
  }));

  const { outlet: outletInfo, getOutletId } = useStoreOutlet((state) => ({
    outlet: state.outlet,
    getOutletId: state.getOutletId,
  }));

  const [subscribeMiniappReq] = useRxdbMiniappReq();

  const hitAPI = useHITApi();

  const getBodyText = () => {
    if (messageDialog) {
      return messageDialog;
    }
    if (getSetting() && getSetting().orderMethod !== 'payment_order') {
      return getSetting().confirmOrderText;
    }
    return t('confirmOrderMessage');
  };

  // Step 1, When User click confirm order
  const handleClickButton = async () => {
    setButtonClick(true);
    setIsTransactionProcessed(false);
    updateMiniappConfirm(false);
    updateMiniappReq(null);
    updateMiniappReqMessage(null);
    if (outletInfo.currency === 'idr' && getSetting().orderMethod === 'payment_order') {
      let confirmTrans = await confirmTransaction();
      console.log(confirmTrans);
    } else {
      ///subscribe storelog kemmudian hit api baru
      const id = new ObjectId().toString();
      updateMiniappConfirmId(id);
      try {
        await insertMiniappStoreLogUtil(hitAPI, getOutletId(), id);
      } catch (error) {
        console.error('Error inserting miniapp storelog:', error);
      }
      subscribeConfirm()
    }
  }


  // Step 3, After miniapp confirm, run this
  const nextTransaction = async () => {
    updateMiniappReqId('')
    if (getSetting().orderMethod === 'quick_order') {
      const id = new ObjectId().toString();
      updateMiniappReqId(id)
      subscribeMiniappReq();
    }
    //subscribe dulu (ready standby) baru confirm client trans

    let confirmTrans = await confirmTransaction();
    const transaction = JSON.parse(confirmTrans);
    if (transaction.status === 'items_unavailable') {
      // Refused before anything was written. CartContext has already closed this overlay
      // and is showing which products to drop, so there is nothing to do here but stop:
      // falling through would report a generic failure over its dialog.
      setButtonClick(false); // no longer processing: the order was refused
      return;
    }
    if (transaction.status === 'success') {
      setStateTransId(transaction.trans_id);
      setstateTransParameter(transaction.parameter);
      if (getSetting().orderMethod === 'normal_order' || getSetting().orderMethod === 'waiter_order') {
        setTimeout(() => {
          updateViewBillId(transaction.trans_id);
          localStorage.setItem(localStorageNames.VIEW_BILL_ID, transaction.trans_id);
          clearCart();
          setConfirmed(true);
        }, 200);
      }
      // Mark transaction as processed after all state updates
      setIsTransactionProcessed(true);
    } else {
      setButtonClick(false);
      openErrorSnackBar(t('transactionFailed'));
    }
  };

  // UseEffect#1
  // Step 2, Wait for miniapp confirmation, if confirmed, do nextTransaction()
  useEffect(() => {
    if (getMiniappConfirm()) {
      setTimeout(() => {
        nextTransaction();
      }, 200);
    }
    // eslint-disable-next-line
  }, [getMiniappConfirm()]);

  // UseEffect#2
  // Step 4, After nextTransaction completes, wait for getMiniappReq() to be updated
  useEffect(() => {
    console.log(stateTransParameter, getMiniappReq(), isTransactionProcessed);
    if (isTransactionProcessed && getMiniappConfirm() && getSetting().orderMethod === 'quick_order') {
      if (getMiniappReq() === 'updated') {
        setTimeout(() => {
          updateViewBillId(stateTransId);
          localStorage.setItem(localStorageNames.VIEW_BILL_ID, stateTransId);
          setConfirmed(true);
          setOpenQRcode({
            visible: true,
            data: stateTransParameter.trim(),
          });
          clearCart();
          setIsTransactionProcessed(false); // Reset flag
        }, 200);
        updateMiniappConfirm(false);
      } else if (getMiniappReq() && getMiniappReq() !== 'updated') {
        closeModal();
        setButtonClick(false);
        openErrorSnackBar(getMiniappReqMessage() || t('transactionFailed'));
        setIsTransactionProcessed(false); // Reset flag
        updateMiniappConfirm(false);
      }
    }
    // eslint-disable-next-line
  }, [getMiniappReq(), isTransactionProcessed]);

  return (
    <Grid direction="column" id="content">
      <Grid item>
        <Typography className={classes.confirmText}>
          {isButtonClick ? t('waitingOrder') : getBodyText()}
        </Typography>
      </Grid>
      {isButtonClick ? (
        <Grid
          direction="column"
          justify="center"
          alignItems="center"
          sx={{ textAlign: 'center', paddingTop: theme.spacing(3) }}>
          <CircularProgress sx={{ color: appColor.primaryFontColor }} />
          <Box sx={{ marginTop: theme.spacing(2) }}>
            {getLocalState('messageRetry') && (
              <Typography className={classes.confirmText}>
                {t('retryConfirm')}
                {getLocalState('messageRetry')}
              </Typography>
            )}
          </Box>
        </Grid>
      ) : (
        <Grid
          container
          direction="row"
          sx={{
            textAlign: 'center',
            display: 'center',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            marginTop: '1rem',
          }}>
          <Grid item sx={{ width: '50%' }}>
            <Button
              onClick={closeModal}
              className={classes.buttonConfirm}
              sx={{
                color: 'black',
                height: getSetting().orderMethod !== 'payment_order' && 'auto',
                backgroundColor: '#e0e0e0',
                width: '95%',
                marginRight: theme.spacing(2),
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
              variant="contained">
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item sx={{ width: '50%' }}>
            <Button
              id="confirmOrder"
              onClick={handleClickButton}
              className={classes.buttonConfirm}
              sx={{
                color: appColor.secondaryFontColor,
                backgroundColor: appColor.primaryThemeColor,
                height: getSetting().orderMethod !== 'payment_order' && 'auto',
                width: '95%',
                marginLeft: theme.spacing(2),
                '&:hover': {
                  backgroundColor: appColor.primaryThemeColor,
                },
              }}
              variant="contained">
              {`${t('confirm')}`}
            </Button>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
export default ConfirmDialog;
